<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useCarBookingStore } from 'dashboard/stores/carBooking';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  conversationId: { type: [String, Number], required: true },
});

const { t } = useI18n();
const carBooking = useCarBookingStore();

const session = computed(() => carBooking.sessionFor(props.conversationId));
const uiFlags = computed(() => carBooking.uiFlags);
const entries = computed(() => session.value.compare || []);

// An entry taken before the most recent search still shows the price it was saved
// at. It is kept (that is the point of a snapshot) but flagged, because acting on
// it needs a fresh search to re-resolve the offer.
const isStale = entry => entry.searchId !== session.value.searchId;

const rentalDays = computed(() => {
  const { pickupDate, dropoffDate } = session.value.form || {};
  if (!pickupDate || !dropoffDate) return 0;
  const diff =
    (new Date(`${dropoffDate}T00:00:00`) - new Date(`${pickupDate}T00:00:00`)) /
    86400000;
  return Math.max(Math.round(diff), 0);
});

const perDay = entry => {
  if (!rentalDays.value || !entry.offer.amount) return '';
  return `${Math.round(entry.offer.amount / rentalDays.value)} ${entry.offer.currency}`;
};

// Cheapest wins on the charge amount only; mixing currencies would make the
// comparison meaningless, so the badge is withheld when they differ.
const cheapestKey = computed(() => {
  const priced = entries.value.filter(entry => entry.offer.amount != null);
  if (priced.length < 2) return null;
  const currencies = new Set(priced.map(entry => entry.offer.currency));
  if (currencies.size > 1) return null;
  return priced.reduce((best, entry) =>
    entry.offer.amount < best.offer.amount ? entry : best
  ).key;
});

const remove = key => carBooking.removeCompare(props.conversationId, key);
const clearAll = () => carBooking.clearCompare(props.conversationId);
const back = () => carBooking.setStep(props.conversationId, 'offers');

const openDetail = entry =>
  carBooking.openDetail(props.conversationId, entry.token);

// A stale entry's token belongs to an older search, so re-run the search first and
// only open the offer if that exact vehicle and rate plan came back.
const refreshAndOpen = async entry => {
  try {
    await carBooking.searchFromForm(props.conversationId);
  } catch (error) {
    useAlert(
      error.response?.data?.detail ||
        t('CONVERSATION.CAR_BOOKING.TRIP.NO_AVAILABILITY')
    );
    return;
  }
  const fresh = (session.value.compare || []).find(
    item => item.key === entry.key
  );
  if (!fresh || fresh.searchId !== session.value.searchId) {
    useAlert(t('CONVERSATION.CAR_BOOKING.COMPARE.GONE'));
    return;
  }
  carBooking.setStep(props.conversationId, 'compare');
  openDetail(fresh);
};

const rows = computed(() => [
  {
    key: 'provider',
    label: t('CONVERSATION.CAR_BOOKING.COMPARE.ROW_PROVIDER'),
    value: entry => entry.provider,
  },
  {
    key: 'ratePlan',
    label: t('CONVERSATION.CAR_BOOKING.COMPARE.ROW_RATE_PLAN'),
    value: entry => entry.ratePlan || '—',
  },
  {
    key: 'category',
    label: t('CONVERSATION.CAR_BOOKING.COMPARE.ROW_CATEGORY'),
    value: entry => entry.offer.category || '—',
  },
  {
    key: 'seats',
    label: t('CONVERSATION.CAR_BOOKING.COMPARE.ROW_SEATS'),
    value: entry => entry.offer.vehicle?.seats ?? '—',
  },
  {
    key: 'bags',
    label: t('CONVERSATION.CAR_BOOKING.COMPARE.ROW_BAGS'),
    value: entry => entry.offer.vehicle?.bags ?? '—',
  },
  {
    key: 'transmission',
    label: t('CONVERSATION.CAR_BOOKING.COMPARE.ROW_TRANSMISSION'),
    value: entry => entry.offer.vehicle?.transmission || '—',
  },
  {
    key: 'mileage',
    label: t('CONVERSATION.CAR_BOOKING.COMPARE.ROW_MILEAGE'),
    value: entry =>
      entry.offer.unlimitedKm
        ? t('CONVERSATION.CAR_BOOKING.OFFERS.UNLIMITED_KM_CHIP')
        : '—',
  },
  {
    key: 'availability',
    label: t('CONVERSATION.CAR_BOOKING.COMPARE.ROW_AVAILABILITY'),
    value: entry =>
      entry.offer.status === 'FREE_SELL'
        ? t('CONVERSATION.CAR_BOOKING.OFFERS.AVAILABLE')
        : entry.offer.status || '—',
  },
  {
    key: 'perDay',
    label: t('CONVERSATION.CAR_BOOKING.COMPARE.ROW_PER_DAY'),
    value: entry => perDay(entry) || '—',
  },
]);
</script>

<template>
  <div class="flex h-full flex-col gap-4">
    <div v-if="!entries.length" class="flex flex-1 items-center justify-center">
      <p class="m-0 text-sm text-n-slate-11">
        {{ t('CONVERSATION.CAR_BOOKING.COMPARE.EMPTY') }}
      </p>
    </div>

    <div v-else class="flex-1 overflow-auto">
      <table class="w-full border-collapse text-sm">
        <thead>
          <tr>
            <th class="w-40 p-2 text-left align-bottom">
              <span class="text-xs font-semibold uppercase text-n-slate-10">
                {{ t('CONVERSATION.CAR_BOOKING.COMPARE.TITLE') }}
              </span>
            </th>
            <th
              v-for="entry in entries"
              :key="entry.key"
              class="min-w-48 border-l border-n-weak p-3 text-left align-top"
            >
              <div class="flex flex-col gap-2">
                <div
                  class="flex h-20 items-center justify-center rounded-lg bg-n-alpha-2"
                >
                  <img
                    v-if="entry.offer.imageUrl"
                    :src="entry.offer.imageUrl"
                    :alt="entry.offer.sampleModel || entry.offer.category"
                    class="h-full w-full object-contain"
                  />
                  <span v-else class="i-lucide-car size-8 text-n-slate-10" />
                </div>
                <p class="m-0 font-semibold text-n-slate-12">
                  {{ entry.offer.sampleModel || entry.offer.category }}
                </p>
                <p class="m-0 text-xl font-bold text-n-slate-12">
                  {{ entry.offer.amount }} {{ entry.offer.currency }}
                </p>
                <span
                  v-if="cheapestKey === entry.key"
                  class="w-fit rounded-full bg-n-green-3 px-2 py-0.5 text-xs font-semibold text-n-green-11"
                >
                  {{ t('CONVERSATION.CAR_BOOKING.COMPARE.CHEAPEST') }}
                </span>
                <span
                  v-if="isStale(entry)"
                  class="w-fit rounded-full bg-n-amber-3 px-2 py-0.5 text-xs font-semibold text-n-amber-11"
                >
                  {{ t('CONVERSATION.CAR_BOOKING.COMPARE.STALE') }}
                </span>
                <NextButton
                  link
                  slate
                  xs
                  :label="t('CONVERSATION.CAR_BOOKING.COMPARE.REMOVE')"
                  @click="remove(entry.key)"
                />
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="row.key" class="border-t border-n-weak">
            <th
              class="p-2 text-left text-xs font-semibold uppercase text-n-slate-10"
            >
              {{ row.label }}
            </th>
            <td
              v-for="entry in entries"
              :key="entry.key"
              class="border-l border-n-weak p-2 capitalize text-n-slate-12"
            >
              {{ row.value(entry) }}
            </td>
          </tr>
          <tr class="border-t border-n-weak">
            <th />
            <td
              v-for="entry in entries"
              :key="entry.key"
              class="border-l border-n-weak p-2"
            >
              <NextButton
                v-if="isStale(entry)"
                faded
                slate
                sm
                :label="t('CONVERSATION.CAR_BOOKING.COMPARE.REFRESH')"
                :disabled="uiFlags.searching"
                :is-loading="uiFlags.searching"
                @click="refreshAndOpen(entry)"
              />
              <NextButton
                v-else
                solid
                blue
                sm
                :label="t('CONVERSATION.CAR_BOOKING.COMPARE.CHOOSE')"
                @click="openDetail(entry)"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="flex items-center gap-3 border-t border-n-weak pt-3">
      <NextButton
        faded
        slate
        :label="t('CONVERSATION.CAR_BOOKING.OFFERS.BACK')"
        @click="back"
      />
      <NextButton
        v-if="entries.length"
        link
        slate
        class="ml-auto"
        :label="t('CONVERSATION.CAR_BOOKING.COMPARE.CLEAR')"
        @click="clearAll"
      />
    </div>
  </div>
</template>
